<script setup>
import { computed, useAttrs } from 'vue';

import ImageChip from 'next/message/chips/Image.vue';
import VideoChip from 'next/message/chips/Video.vue';
import AudioChip from 'next/message/chips/Audio.vue';
import FileChip from 'next/message/chips/File.vue';
import { useMessageContext } from '../provider.js';

import { ATTACHMENT_TYPES } from '../constants';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */
const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
});

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();
const { orientation } = useMessageContext();

// BonBoZ: qua 9 anh thi bong bong dai het man hinh. O thu 9 deo lop "+N"; bam
// vao van mo GalleryView, ma gallery chay tren TOAN BO dinh kem cua hoi thoai
// nen khong anh nao bi giau di that.
const MAX_MEDIA_TILES = 9;

const classToApply = computed(() => {
  const baseClasses = [attrs.class, 'flex', 'flex-wrap'];

  if (orientation.value === 'right') {
    baseClasses.push('justify-end');
  }

  return baseClasses;
});

const allAttachments = computed(() => {
  return Array.isArray(props.attachments) ? props.attachments : [];
});

const mediaAttachments = computed(() => {
  const allowedTypes = [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO];
  const mediaTypes = allAttachments.value.filter(attachment =>
    allowedTypes.includes(attachment.fileType)
  );

  return mediaTypes.sort(
    (a, b) =>
      allowedTypes.indexOf(a.fileType) - allowedTypes.indexOf(b.fileType)
  );
});

const recordings = computed(() => {
  return allAttachments.value.filter(
    attachment => attachment.fileType === ATTACHMENT_TYPES.AUDIO
  );
});

const files = computed(() => {
  return allAttachments.value.filter(
    attachment => attachment.fileType === ATTACHMENT_TYPES.FILE
  );
});

const isSingleMedia = computed(() => mediaAttachments.value.length === 1);

const visibleMedia = computed(() =>
  mediaAttachments.value.slice(0, MAX_MEDIA_TILES)
);

const hiddenMediaCount = computed(() =>
  Math.max(0, mediaAttachments.value.length - MAX_MEDIA_TILES)
);

// BonBoZ: so cot theo so anh, kieu luoi anh cua Messenger/Pancake.
//   1  -> giu nguyen ti le that, khong cat
//   2  -> hai o vuong canh nhau
//   4  -> luoi 2x2 (dep hon 3+1 le mot o)
//   con lai -> 3 cot
// Chan be ngang o 2 nac tren: mot anh don keo het 480px cua bong bong thi to
// qua so voi doan chu ben canh.
const mediaGridClass = computed(() => {
  const count = mediaAttachments.value.length;
  if (count === 1) return 'grid-cols-1 max-w-[20rem]';
  if (count === 2) return 'grid-cols-2 max-w-[24rem]';
  if (count === 4) return 'grid-cols-2 max-w-[24rem]';
  return 'grid-cols-3';
});
</script>

<template>
  <!-- BonBoZ: anh/video xep thanh LUOI chu khong con la mot hang chip 72px tu
       xuong dong.
       Cach cu: `flex flex-wrap` + o vuong 72px cung nhac. Ba cai sai cung luc —
       anh be bang con tem (anh truoc/sau cua khach nhin khong ra gi), hang cuoi
       le vai o thi voi tin ben phai no bi day sang phai thanh mot manh roi ra,
       va moi o mot goc bo rieng nen ca bo doc thanh mot dam vun.
       Cach moi: luoi co so cot theo so anh, mach 3px, bo goc DUNG MOT LAN o
       vien ngoai — ca bo anh doc ra thanh mot khoi. -->
  <div
    v-if="mediaAttachments.length"
    class="grid gap-[3px] overflow-hidden rounded-2xl w-full"
    :class="[
      mediaGridClass,
      orientation === 'right' ? 'ltr:ml-auto rtl:mr-auto' : '',
    ]"
  >
    <template v-for="(attachment, index) in visibleMedia" :key="attachment.id">
      <ImageChip
        v-if="attachment.fileType === ATTACHMENT_TYPES.IMAGE"
        :attachment="attachment"
        :single="isSingleMedia"
        :remaining="index === visibleMedia.length - 1 ? hiddenMediaCount : 0"
      />
      <VideoChip
        v-else-if="attachment.fileType === ATTACHMENT_TYPES.VIDEO"
        :attachment="attachment"
        :single="isSingleMedia"
        :remaining="index === visibleMedia.length - 1 ? hiddenMediaCount : 0"
      />
    </template>
  </div>
  <div v-if="recordings.length" :class="classToApply">
    <div v-for="attachment in recordings" :key="attachment.id">
      <AudioChip
        class="bg-n-alpha-3 dark:bg-n-alpha-2 text-n-slate-12"
        :attachment="attachment"
      />
    </div>
  </div>
  <div v-if="files.length" :class="classToApply">
    <FileChip
      v-for="attachment in files"
      :key="attachment.id"
      :attachment="attachment"
    />
  </div>
</template>
